import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import Pagination from "../elements/Pagination";
import { getId } from "../../utils/idHelpers";

const LocationImportReview = ({ open, batch, onClose, onCompleted }) => {
  const navigate = useNavigate();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const userId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [stagedRecords, setStagedRecords] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [matchFilter, setMatchFilter] = useState("all");

  const batchId = batch?.batchId;

  useEffect(() => {
    if (!open || !batchId) return;

    const loadStaged = async () => {
      setLoading(true);
      try {
        const results = await executeAction({
          entityName: "LocationImport",
          action: "getStaged",
          filter: { batchId },
        });

        if (results?.isSuccessful && Array.isArray(results.data)) {
          setStagedRecords(results.data);
        } else {
          setStagedRecords([]);
        }
      } catch (error) {
        console.error("[LocationImportReview] Error loading staged:", error);
        showMessage("Failed to load staged records", "error");
      } finally {
        setLoading(false);
      }
    };

    loadStaged();
  }, [open, batchId]);

  const summary = useMemo(() => {
    const total = stagedRecords.length;
    const approved = stagedRecords.filter(
      (item) => item.status === "approved",
    ).length;
    const pending = stagedRecords.filter(
      (item) => item.status === "pending",
    ).length;
    const matched = stagedRecords.filter(
      (item) =>
        item.matchResult?.matchedLocationId &&
        item.matchResult.matchType !== "ambiguous_number",
    ).length;
    const ambiguous = stagedRecords.filter(
      (item) => item.matchResult?.matchType === "ambiguous_number",
    ).length;

    // Counts by match type for filters
    const exactMatch = stagedRecords.filter(
      (item) => item.matchResult?.matchType === "exact_banner_number",
    ).length;
    const addressMatch = stagedRecords.filter(
      (item) => item.matchResult?.matchType === "address_match",
    ).length;
    const numberPostal = stagedRecords.filter(
      (item) => item.matchResult?.matchType === "location_number_postal",
    ).length;
    const noMatch = stagedRecords.filter(
      (item) =>
        !item.matchResult?.matchType || item.matchResult?.matchType === "none",
    ).length;

    return {
      total,
      approved,
      pending,
      matched,
      ambiguous,
      exactMatch,
      addressMatch,
      numberPostal,
      noMatch,
    };
  }, [stagedRecords]);

  // Filtered records based on match type
  const filteredRecords = useMemo(() => {
    if (matchFilter === "all") return stagedRecords;

    return stagedRecords.filter((record) => {
      const matchType = record.matchResult?.matchType;
      switch (matchFilter) {
        case "exact":
          return matchType === "exact_banner_number";
        case "address":
          return matchType === "address_match";
        case "numberPostal":
          return matchType === "location_number_postal";
        case "ambiguous":
          return matchType === "ambiguous_number";
        case "noMatch":
          return !matchType || matchType === "none";
        default:
          return true;
      }
    });
  }, [stagedRecords, matchFilter]);

  // Paginated records for display
  const paginatedRecords = useMemo(() => {
    const startIndex = pageNumber * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filteredRecords.slice(startIndex, endIndex);
  }, [filteredRecords, pageNumber, recordsPerPage]);

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
    setExpandedRows({});
  };

  const handleRecordsPerPageChange = (newSize) => {
    setRecordsPerPage(newSize);
    setPageNumber(0);
    setExpandedRows({});
  };

  const handleMatchFilterChange = (filter) => {
    setMatchFilter(filter);
    setPageNumber(0);
    setExpandedRows({});
  };

  const toggleExpandRow = (recordId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [recordId]: !prev[recordId],
    }));
  };

  const handleApproveRecord = async (recordId) => {
    setApprovingId(recordId);
    try {
      await executeAction({
        entityName: "LocationImport",
        action: "updateRecordStatus",
        data: { recordId, status: "approved" },
      });

      setStagedRecords((prev) =>
        prev.map((r) =>
          getId(r) === recordId ? { ...r, status: "approved" } : r,
        ),
      );
      showMessage("Record approved");
    } catch (error) {
      showMessage(`Failed to approve record: ${error.message}`, "error");
    } finally {
      setApprovingId(null);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    setDeletingId(recordId);
    try {
      await executeAction({
        entityName: "LocationImport",
        action: "deleteRecord",
        data: { recordId },
      });

      setStagedRecords((prev) => prev.filter((r) => getId(r) !== recordId));
      showMessage("Record deleted");
    } catch (error) {
      showMessage(`Failed to delete record: ${error.message}`, "error");
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  const handleApproveAll = async () => {
    if (!batchId) return;
    setProcessing(true);
    try {
      const result = await executeAction({
        entityName: "LocationImport",
        action: "approve",
        data: { batchId },
      });

      if (result?.isSuccessful === false) {
        showMessage(result?.message || "Approve failed", "error");
        return;
      }

      showMessage("All approved records processed");
      onCompleted?.();
      onClose();
    } catch (error) {
      showMessage(`Approve failed: ${error.message}`, "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectAll = async () => {
    if (!batchId) return;
    setProcessing(true);
    try {
      const result = await executeAction({
        entityName: "LocationImport",
        action: "reject",
        data: { batchId },
      });

      if (result?.isSuccessful === false) {
        showMessage(result?.message || "Reject failed", "error");
        return;
      }

      showMessage("Batch rejected");
      onCompleted?.();
      onClose();
    } catch (error) {
      showMessage(`Reject failed: ${error.message}`, "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleExportMatched = async () => {
    const matchedIds = Array.from(
      new Set(
        stagedRecords
          .map((record) => record.matchResult?.matchedLocationId)
          .filter(Boolean)
          .map((id) => (id.toString ? id.toString() : id)),
      ),
    );

    if (matchedIds.length === 0) {
      showMessage("No matched locations to export", "warning");
      return;
    }

    try {
      const results = await executeAction({
        entityName: "WorkQueue",
        action: "createSelection",
        data: {
          workType: "exportLocations",
          ids: matchedIds,
          userId,
          email,
        },
        displayMessage: false,
      });

      if (!results?.isSuccessful || !results.data?.selectionId) {
        showMessage("Unable to create export selection", "error");
        return;
      }

      navigate("/selectedlocations", {
        state: {
          selectionId: results.data.selectionId,
        },
      });
    } catch (error) {
      showMessage(`Export failed: ${error.message}`, "error");
    }
  };

  const handleExportWorkbook = async () => {
    if (!batchId) return;

    setProcessing(true);
    try {
      const result = await executeAction({
        entityName: "LocationImport",
        action: "exportWorkbook",
        data: { batchId, email },
      });

      if (result?.isSuccessful) {
        showMessage(
          "Workbook export started. You'll receive it via email shortly.",
          "success",
        );
      } else {
        showMessage(result?.message || "Export workbook failed", "error");
      }
    } catch (error) {
      showMessage(`Export workbook failed: ${error.message}`, "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleAddAllNewLocations = async () => {
    if (!batchId) return;

    setProcessing(true);
    try {
      const result = await executeAction({
        entityName: "LocationImport",
        action: "addAllNewLocations",
        data: { batchId, email },
      });

      if (result?.isSuccessful) {
        showMessage(
          "Location creation started. Processing will complete in the background.",
          "success",
        );
        // Optionally close dialog or refresh
        onCompleted?.();
      } else {
        showMessage(result?.message || "Add new locations failed", "error");
      }
    } catch (error) {
      showMessage(`Add new locations failed: ${error.message}`, "error");
    } finally {
      setProcessing(false);
    }
  };

  const formatAddress = (address) => {
    if (!address) return "No address";
    const parts = [
      address.streetAddress || address.streetName,
      address.city,
      address.province,
      address.postal,
    ].filter(Boolean);
    return parts.join(", ");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Review Import Batch</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{ mt: 1 }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600 }}
            >
              {batch?.batchName || "Import Batch"}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Batch ID: {batchId || ""}
            </Typography>
          </Box>

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
            >
              <Chip
                label={`Total: ${summary.total}`}
                color="default"
              />
              <Chip
                label={`Approved: ${summary.approved}`}
                color="success"
              />
              <Chip
                label={`Pending: ${summary.pending}`}
                color="warning"
              />
              <Chip
                label={`Matched: ${summary.matched}`}
                color="info"
              />
              {summary.ambiguous > 0 && (
                <Chip
                  label={`Ambiguous: ${summary.ambiguous}`}
                  color="error"
                />
              )}
            </Stack>
          )}

          {!loading && stagedRecords.length > 0 && (
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 600 }}
              >
                Filter by Match Type:
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
              >
                <Chip
                  label={`All (${summary.total})`}
                  color={matchFilter === "all" ? "primary" : "default"}
                  onClick={() => handleMatchFilterChange("all")}
                  variant={matchFilter === "all" ? "filled" : "outlined"}
                  clickable
                />
                <Chip
                  label={`Exact Match (${summary.exactMatch})`}
                  color={matchFilter === "exact" ? "success" : "default"}
                  onClick={() => handleMatchFilterChange("exact")}
                  variant={matchFilter === "exact" ? "filled" : "outlined"}
                  clickable
                />
                <Chip
                  label={`Address Match (${summary.addressMatch})`}
                  color={matchFilter === "address" ? "success" : "default"}
                  onClick={() => handleMatchFilterChange("address")}
                  variant={matchFilter === "address" ? "filled" : "outlined"}
                  clickable
                />
                <Chip
                  label={`Number+Postal (${summary.numberPostal})`}
                  color={matchFilter === "numberPostal" ? "info" : "default"}
                  onClick={() => handleMatchFilterChange("numberPostal")}
                  variant={
                    matchFilter === "numberPostal" ? "filled" : "outlined"
                  }
                  clickable
                />
                {summary.ambiguous > 0 && (
                  <Chip
                    label={`Ambiguous (${summary.ambiguous})`}
                    color={matchFilter === "ambiguous" ? "warning" : "default"}
                    onClick={() => handleMatchFilterChange("ambiguous")}
                    variant={
                      matchFilter === "ambiguous" ? "filled" : "outlined"
                    }
                    clickable
                  />
                )}
                <Chip
                  label={`No Match (${summary.noMatch})`}
                  color={matchFilter === "noMatch" ? "default" : "default"}
                  onClick={() => handleMatchFilterChange("noMatch")}
                  variant={matchFilter === "noMatch" ? "filled" : "outlined"}
                  clickable
                />
              </Stack>
            </Box>
          )}

          {!loading && stagedRecords.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell width="50" />
                  <TableCell>Import Record</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecords.map((record) => {
                  const recordId = getId(record);
                  const mapped = record.mappedFields || {};
                  const parsed = record.parsedAddress || {};
                  const matched = record.matchedLocation;
                  const isExpanded = expandedRows[recordId];
                  const matchType = record.matchResult?.matchType || "none";
                  const confidenceScore =
                    record.matchResult?.confidenceScore || 0;

                  let matchChip = null;
                  if (matchType === "exact_banner_number") {
                    matchChip = (
                      <Chip
                        label="Exact Match"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    );
                  } else if (matchType === "address_match") {
                    matchChip = (
                      <Chip
                        label="Address Match"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    );
                  } else if (matchType === "location_number_postal") {
                    matchChip = (
                      <Chip
                        label="Number + Postal"
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    );
                  } else if (matchType === "ambiguous_number") {
                    matchChip = (
                      <Chip
                        label="Ambiguous"
                        color="warning"
                        size="small"
                        variant="outlined"
                      />
                    );
                  } else {
                    matchChip = (
                      <Chip
                        label="No Match"
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    );
                  }

                  return [
                    <TableRow key={`row-${recordId}`}>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpandRow(recordId)}
                        >
                          <ExpandMoreIcon
                            sx={{
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                          >
                            {mapped.locationName || "(Unnamed)"}
                            {mapped.locationNumber
                              ? ` (#${mapped.locationNumber})`
                              : ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                          >
                            {formatAddress(parsed)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                          >
                            Territory: {mapped.territory || "(None)"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{matchChip}</TableCell>
                      <TableCell>
                        <Chip
                          label={record.status || "pending"}
                          color={
                            record.status === "approved" ? "success" : "default"
                          }
                          size="small"
                          icon={
                            record.status === "approved" ? (
                              <CheckCircleIcon />
                            ) : undefined
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => setConfirmDelete(recordId)}
                          disabled={deletingId === recordId}
                        >
                          {deletingId === recordId ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>,
                    isExpanded && (
                      <TableRow key={`expanded-${recordId}`}>
                        <TableCell colSpan={5}>
                          <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
                            <Stack
                              direction="row"
                              spacing={4}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600, mb: 1 }}
                                >
                                  Import Details
                                </Typography>
                                <Typography variant="body2">
                                  Location: {mapped.locationName || "(Unnamed)"}
                                </Typography>
                                <Typography variant="body2">
                                  Number: {mapped.locationNumber || "(None)"}
                                </Typography>
                                <Typography variant="body2">
                                  Address: {formatAddress(parsed)}
                                </Typography>
                                {mapped.bannerName && (
                                  <Typography variant="body2">
                                    Banner: {mapped.bannerName}
                                  </Typography>
                                )}
                                <Typography variant="body2">
                                  Territory: {mapped.territory || "(None)"}
                                </Typography>
                              </Box>

                              <Box sx={{ flex: 1 }}>
                                {matchType === "ambiguous_number" &&
                                record.matchResult?.alternativeMatches?.length >
                                  0 ? (
                                  <>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 600, mb: 1 }}
                                    >
                                      Found{" "}
                                      {
                                        record.matchResult.alternativeMatches
                                          .length
                                      }{" "}
                                      Candidates
                                    </Typography>
                                    <Stack spacing={1}>
                                      {record.matchResult.alternativeMatches.map(
                                        (alt, idx) => (
                                          <Box
                                            key={idx}
                                            sx={{
                                              p: 1,
                                              border: "1px solid #ddd",
                                              borderRadius: 1,
                                              backgroundColor: "#fff",
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: 600 }}
                                            >
                                              {alt.name}
                                              {alt.locationNumber
                                                ? ` (#${alt.locationNumber})`
                                                : ""}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="textSecondary"
                                            >
                                              Banner: {alt.banner}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="textSecondary"
                                              display="block"
                                            >
                                              Address:{" "}
                                              {formatAddress(alt.address)}
                                            </Typography>
                                          </Box>
                                        ),
                                      )}
                                    </Stack>
                                  </>
                                ) : (
                                  <>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 600, mb: 1 }}
                                    >
                                      Matched Location
                                    </Typography>
                                    {matched && matched.id ? (
                                      <Stack spacing={0.5}>
                                        <Typography variant="body2">
                                          Name: {matched.name}
                                        </Typography>
                                        <Typography variant="body2">
                                          Number:{" "}
                                          {matched.locationNumber || "(None)"}
                                        </Typography>
                                        <Typography variant="body2">
                                          Address:{" "}
                                          {formatAddress(matched.address)}
                                        </Typography>
                                        {matched.banner && (
                                          <Typography variant="body2">
                                            Banner: {matched.banner}
                                          </Typography>
                                        )}
                                      </Stack>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        Not Matched
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ),
                  ].filter(Boolean);
                })}
              </TableBody>
            </Table>
          )}

          {!loading && stagedRecords.length === 0 && (
            <Typography
              color="textSecondary"
              align="center"
              sx={{ py: 3 }}
            >
              No records to review.
            </Typography>
          )}

          {!loading && stagedRecords.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Pagination
                pageNumber={pageNumber}
                recordsPerPage={recordsPerPage}
                totalCount={filteredRecords.length}
                onPageChange={handlePageChange}
                onRecordsPerPageChange={handleRecordsPerPageChange}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={processing}
        >
          Close
        </Button>
        <Button
          onClick={handleExportWorkbook}
          disabled={processing || loading}
        >
          Export Workbook
        </Button>
        <Button
          onClick={handleAddAllNewLocations}
          disabled={processing || loading}
        >
          Add All New
        </Button>
        <Button
          onClick={handleExportMatched}
          disabled={processing || loading}
        >
          Export Matched Template
        </Button>
      </DialogActions>

      <Dialog_YesNo
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Delete record?"
        message="Delete this staging record? This cannot be undone."
        onYes={async () => {
          const recordId = confirmDelete;
          setConfirmDelete(null);
          await handleDeleteRecord(recordId);
        }}
        onNo={() => setConfirmDelete(null)}
      />
    </Dialog>
  );
};

export default LocationImportReview;
